import React, { useEffect, useMemo, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { X, Printer } from "lucide-react";
import api, { formatTL } from "../lib/api";
import { toast } from "sonner";

/**
 * Niimbot 40x30mm barcode label modal.
 * Two templates: 6-per-label (2×3) and 10-per-label (2×5), same product only.
 * Each cell shows Code 128 barcode + SKU + sale price. NO product name.
 */
export default function LabelPrintModal({ product, onClose }) {
  const [tpl, setTpl] = useState(6);
  const [fresh, setFresh] = useState(product);
  const printRef = useRef(null);

  // Refetch to guarantee current sale_price on print
  useEffect(() => {
    if (!product?.id) return;
    api
      .get(`/label/product/${product.id}`)
      .then((r) => setFresh({ ...product, ...r.data }))
      .catch(() => {});
  }, [product?.id]);

  const cells = useMemo(() => Array.from({ length: tpl }), [tpl]);
  const cols = 2;
  const rows = tpl === 6 ? 3 : 5;

  const doPrint = () => {
    window.print();
  };

  if (!fresh) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 print:static print:bg-transparent print:p-0">
      <div
        className="bg-white rounded-2xl w-full max-w-2xl border border-stone-200 p-5 max-h-[95vh] overflow-y-auto print:hidden"
        data-testid="label-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
              NIIMBOT 40×30
            </div>
            <div className="font-serif-display text-2xl">Etiket Yazdır</div>
          </div>
          <button
            onClick={onClose}
            data-testid="label-close"
            className="p-1 rounded-md hover:bg-stone-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <Info label="Ürün (yalnızca referans)" value={fresh.name} />
          <Info label="SKU" value={<span className="font-mono-code">{fresh.sku}</span>} />
          <Info label="Barkod" value={<span className="font-mono-code">{fresh.barcode}</span>} />
          <Info label="Satış Fiyatı" value={formatTL(fresh.sale_price)} />
        </div>

        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">
            Şablon
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="tpl-6"
              onClick={() => setTpl(6)}
              className={`p-3 rounded-xl border text-sm ${
                tpl === 6
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white border-stone-200 hover:bg-stone-50"
              }`}
            >
              <span className="font-serif-display text-lg">6'lı Barkod</span>
              <div className="text-[11px] opacity-70">2 sütun × 3 satır</div>
            </button>
            <button
              data-testid="tpl-10"
              onClick={() => setTpl(10)}
              className={`p-3 rounded-xl border text-sm ${
                tpl === 10
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white border-stone-200 hover:bg-stone-50"
              }`}
            >
              <span className="font-serif-display text-lg">10'lu Barkod</span>
              <div className="text-[11px] opacity-70">2 sütun × 5 satır</div>
            </button>
          </div>
        </div>

        {/* On-screen preview (kept in physical 40×30 aspect via 6× scale) */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">
            Baskı Önizleme (yaklaşık ölçek)
          </div>
          <div className="flex justify-center">
            <div
              className="border border-stone-300 rounded-md shadow-inner bg-white"
              style={{ width: "240px", height: "180px" }}
              data-testid="label-preview"
            >
              <LabelSheet product={fresh} cells={cells} cols={cols} rows={rows} preview />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-stone-200 text-sm"
          >
            İptal
          </button>
          <button
            data-testid="label-print"
            onClick={doPrint}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-white text-sm"
          >
            <Printer className="w-4 h-4" />
            Yazdır
          </button>
        </div>
      </div>

      {/* Hidden print-only 40×30mm sheet */}
      <div ref={printRef} className="print-sheet-wrapper" data-testid="label-print-sheet">
        <LabelSheet product={fresh} cells={cells} cols={cols} rows={rows} />
      </div>

      <style>{`
        .print-sheet-wrapper { display: none; }
        @media print {
          @page {
            size: 40mm 30mm;
            margin: 0;
          }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          body * { visibility: hidden !important; }
          .print-sheet-wrapper, .print-sheet-wrapper * { visibility: visible !important; }
          .print-sheet-wrapper {
            display: block !important;
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 40mm !important;
            height: 30mm !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-stone-500 tracking-widest">{label}</div>
      <div className="text-stone-900 truncate">{value}</div>
    </div>
  );
}

/**
 * Physical 40×30 mm label sheet.
 * On screen (preview): fills its parent container (aspect ~4:3 already provided).
 * On print: exact 40mm × 30mm via inline style using CSS mm units.
 */
function LabelSheet({ product, cells, cols, rows, preview = false }) {
  const wrapStyle = preview
    ? { width: "100%", height: "100%", padding: "1.5mm" }
    : { width: "40mm", height: "30mm", padding: "0.6mm", boxSizing: "border-box", background: "white" };
  return (
    <div
      style={{
        ...wrapStyle,
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: preview ? "1mm" : "0.4mm",
      }}
    >
      {cells.map((_, i) => (
        <BarcodeCell key={i} product={product} preview={preview} tpl={cells.length} />
      ))}
    </div>
  );
}

function BarcodeCell({ product, preview, tpl }) {
  const svgRef = useRef(null);
  useEffect(() => {
    if (!svgRef.current || !product?.barcode) return;
    try {
      // For 10-per-label the cell is very short — reduce module width slightly
      // but keep quiet zones and contrast for scanability.
      const opts = tpl === 10
        ? { format: "CODE128", width: preview ? 1 : 1.1, height: preview ? 22 : 26, displayValue: false, margin: 0, background: "#ffffff", lineColor: "#000000" }
        : { format: "CODE128", width: preview ? 1.2 : 1.4, height: preview ? 30 : 34, displayValue: false, margin: 0, background: "#ffffff", lineColor: "#000000" };
      JsBarcode(svgRef.current, product.barcode, opts);
    } catch (e) {
      // ignore
    }
  }, [product?.barcode, preview, tpl]);

  const priceStr = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
    Number(product?.sale_price || 0)
  );

  const cellStyle = preview
    ? { padding: "0.5mm" }
    : { padding: "0.15mm" };

  // Text sizes: barcodes must remain scannable, so text is small
  const skuFont = preview
    ? (tpl === 10 ? "5.5px" : "7px")
    : (tpl === 10 ? "1.55mm" : "2mm");
  const priceFont = preview
    ? (tpl === 10 ? "6px" : "8px")
    : (tpl === 10 ? "1.7mm" : "2.2mm");

  return (
    <div
      style={{
        ...cellStyle,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: preview ? "0.4mm" : "0.15mm",
        overflow: "hidden",
      }}
    >
      <svg ref={svgRef} style={{ width: "100%", height: preview ? "60%" : "62%" }} />
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: skuFont,
          lineHeight: 1,
          color: "#000",
          letterSpacing: "0.02em",
        }}
      >
        {product.sku}
      </div>
      <div
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 700,
          fontSize: priceFont,
          lineHeight: 1,
          color: "#000",
        }}
      >
        {priceStr} TL
      </div>
    </div>
  );
}
